import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import { Newspaper, Calendar, Mail, TrendingUp } from 'lucide-react';

const AdminDashboard = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [stats, setStats] = useState({
    newsCount: 0,
    bookingsCount: 0,
    contactsCount: 0,
    pendingBookings: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [newsRes, bookingsRes, contactsRes] = await Promise.all([
          api.get('/news?published=true'),
          api.get('/bookings'),
          api.get('/contact')
        ]);

        const pendingBookings = bookingsRes.data.filter(b => b.status === 'pending').length;

        setStats({
          newsCount: newsRes.data.length,
          bookingsCount: bookingsRes.data.length,
          contactsCount: contactsRes.data.length,
          pendingBookings
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    { label: 'Total News', value: stats.newsCount, icon: Newspaper, color: 'bg-blue-500' },
    { label: 'Total Bookings', value: stats.bookingsCount, icon: Calendar, color: 'bg-green-500' },
    { label: 'Pending Bookings', value: stats.pendingBookings, icon: TrendingUp, color: 'bg-yellow-500' },
    { label: 'Contact Requests', value: stats.contactsCount, icon: Mail, color: 'bg-purple-500' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-3xl font-bold mb-8">{t('admin.dashboard')}</h1>

      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">{t('common.loading')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="bg-white rounded-lg shadow p-6" data-testid={`stat-card-${index}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">{stat.label}</p>
                    <p className="text-3xl font-bold mt-1">{stat.value}</p>
                  </div>
                  <div className={`${stat.color} rounded-lg p-3`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Quick Links</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a href="/admin/news" className="block p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
            <h3 className="font-semibold text-blue-900">Manage News</h3>
            <p className="text-sm text-blue-700">Create and edit news articles</p>
          </a>
          <a href="/admin/bookings" className="block p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
            <h3 className="font-semibold text-green-900">View Bookings</h3>
            <p className="text-sm text-green-700">Manage booking requests</p>
          </a>
          <a href="/admin/images" className="block p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
            <h3 className="font-semibold text-purple-900">Upload Images</h3>
            <p className="text-sm text-purple-700">Manage image gallery</p>
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;